<script setup lang="ts">
import { TaskInJobDto, ReferredEntityChange } from '@/api/generator';
import IdentifierLabel from '@/components/IdentifierLabel/IdentifierLabel.vue';
import { I18nKey } from '@/utils/typings';
import StateOfUseBadge from '@/components/stateOfUse/StateOfUseBadge.vue';
import InlineValidationError from '@/components/forms/InlineValidationError/InlineValidationError.vue';
import { getValidationErrorType } from '../utils/jobConfigurationTaskHelpers';
import { useFieldValue } from 'vee-validate';
import { computed } from 'vue';
import { InlineValidationType } from '@/components/forms/InlineValidationError/types';
import { useTranslation } from 'i18next-vue';

const { t } = useTranslation();

const props = defineProps<{
  taskInJob: TaskInJobDto;
}>();

const tasks = useFieldValue<TaskInJobDto[]>('tasks');

const hasIncompatibleWorkItem = computed(() => {
  return (
    props.taskInJob.taskAreaChange === ReferredEntityChange.NotAvailable ||
    props.taskInJob.taskAreaChange === ReferredEntityChange.Deleted
  );
});

const validationErrorInfo = computed<
  | {
      name: string;
      type?: InlineValidationType;
      customMessage?: string;
    }
  | undefined
>(() => {
  const index =
    tasks.value?.findIndex((formTask) => formTask.taskId === props.taskInJob.taskId) ?? -1;

  return index >= 0
    ? {
        name: `tasks[${index}]`,
        type: getValidationErrorType(props.taskInJob),
        customMessage: hasIncompatibleWorkItem.value
          ? t('Validation.JobConfigurationTaskItemAreaWasRemoved' as I18nKey)
          : undefined,
      }
    : undefined;
});
</script>

<template>
  <div class="d-flex">
    <IdentifierLabel
      :label="taskInJob.name"
      :identifier="taskInJob.iuid"
      :subline="$t(`TaskTypeDescriptionEnum.${taskInJob.taskType}` as I18nKey)"
    />
    <div class="d-flex align-center ml-2 ms-auto">
      <InlineValidationError
        v-if="validationErrorInfo"
        validateOnMount
        :name="validationErrorInfo.name"
        :type="validationErrorInfo.type"
        variant="icon-only"
        ignoreTouched
        :customChildErrorMessage="validationErrorInfo.customMessage"
        class="mr-2"
      />
      <StateOfUseBadge :value="taskInJob.state" />
    </div>
  </div>

  <p class="text-truncate text-caption mb-0 text-primary font-weight-medium">
    {{ taskInJob.description }}
  </p>
</template>
